import axiosInstance from "./axios.instance";
import { getSupportticketApiUrl } from "./supportticketBase";

const DEFAULT_PAGE_SIZE = 20;

/**
 * @typedef {Object} TicketChatHistoryParams
 * @property {number|string} supportTicketId
 * @property {number|string} [companyId]
 * @property {number} [page]
 * @property {number} [limit]
 */

/**
 * @typedef {Object} TicketChatMessage
 * @property {number|string|null} id
 * @property {string} message
 * @property {string} messageType
 * @property {string} createdAt
 * @property {string} senderName
 * @property {string} senderRole
 * @property {boolean} [pending]
 * @property {string} [clientTempId]
 */

const normalizeApiMessages = (payload) => {
  const candidates = [
    payload?.data?.rows,
    payload?.data?.messages,
    payload?.rows,
    payload?.messages,
    payload?.data,
  ];
  return candidates.find((item) => Array.isArray(item)) || [];
};

const resolveTotalCount = (payload, fallbackLength) => {
  const countCandidates = [
    payload?.data?.count,
    payload?.count,
    payload?.data?.total,
    payload?.total,
  ];
  const total = countCandidates.find((item) => Number.isFinite(Number(item)));
  if (total !== undefined) return Number(total);
  return fallbackLength;
};

const normalizeError = (error) => {
  const status = error?.response?.status;
  const messageFromApi =
    error?.response?.data?.message ||
    error?.response?.data?.error ||
    error?.message ||
    "Unable to fetch ticket chat messages.";

  if (status === 401 || status === 403) {
    return "You do not have permission to view this ticket chat.";
  }
  if (status === 404) {
    return "Ticket chat not found for the selected ticket/company.";
  }
  return messageFromApi;
};

export const fetchTicketChatMessages = async ({
  supportTicketId,
  companyId,
  page = 0,
  limit = DEFAULT_PAGE_SIZE,
}) => {
  try {
    let response;
    try {
      response = await axiosInstance.get(
        getSupportticketApiUrl(`/admin/supportticket/${supportTicketId}/chat/messages`),
        {
          params: {
            companyId: companyId ?? undefined,
            page,
            limit,
          },
        }
      );
    } catch (error) {
      if (error?.response?.status !== 404) throw error;
      response = await axiosInstance.get(
        `/admin/company/supportTicket/${supportTicketId}/chat/messages`,
        {
          params: {
            companyId: companyId ?? undefined,
            page,
            limit,
          },
        }
      );
    }

    const payload = response?.data || {};
    const rows = normalizeApiMessages(payload);
    const count = resolveTotalCount(payload, rows.length);

    return {
      rows,
      count,
      page,
      limit,
    };
  } catch (error) {
    const normalizedError = new Error(normalizeError(error));
    normalizedError.original = error;
    throw normalizedError;
  }
};

export { DEFAULT_PAGE_SIZE };
